/****************************************************
 * Module Name:  MixReportAvg.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import MixBucketProxy from "../../utility/proxy/manager/mixProxy/mixBucketProxy";
import Constants from "../../utility/constants";
import renderCellExpand from "../common/dataGridCellExpand";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import Utility from "../../utility/utility";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CommonSnackbar from "../common/commonSnackBar";
import { Stack } from "react-bootstrap";
import NoPermission from "../common/noPermission";
import StoneDetailModal from "../common/stoneDetailModal";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import PageTitle from "../common/pageTitle";

const MixReportAvg = () => {

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const [reportLoader, setReportLoader] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [detailReportData, setDetailReportData] = useState([]);

    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    const [mixAvgPricePermission, setMixAvgPricePermission] = useState(false);

    // eslint-disable-next-line
    const [local_permision, setLocal_Permission] = useState(Constants.USER_PERMISSION.filter(
        (iPermission) =>
            iPermission.permissionid === Constants.PERMISSIONS.MIX_REPORT_MIXAVG
    ))

    useEffect(() => {
        PageTitle("MIX_REPORT_MIXAVG");
        handleSearch();
    }, []);

    const handleSearch = () => {
        setReportLoader(true);
        MixBucketProxy.GetMixAvgReport(response => {
            if (response) {
                Constants.USER_COLUMN_PERMISSION.forEach(iColumnPermission => {
                    if (iColumnPermission === "809a4ec3-4c31-4077-b96d-97d53d57efac") {
                        setMixAvgPricePermission(true);
                    }
                })
                setReportData(response);
                handleArray(response);
                setReportLoader(false);
            } else {
                setShowSnackbar(true);
                setSnackbarVariant("error");
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setReportLoader(false);
            }
        });
    };

    const handleArray = (reportData) => {
        let tempDetailData = [];
        if (reportData.length > 0) {
            reportData.map((iReport) => {
                iReport.mixstones.map((mixstone) => {
                    tempDetailData.push(mixstone);
                    return true;
                });
                return true;
            });
        }
        setDetailReportData(tempDetailData);
    }

    const detailColumn = [
        {
            headerName: "KAPAN ID",
            field: "kapanid",
            headerAlign: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: renderCellExpand
        },
        {
            headerName: "PACKET ID",
            field: "packetid",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            headerName: "STONE ID",
            field: "stoneid",
            headerAlign: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>
        },
        {
            headerName: "CREATED AT",
            field: "createdat",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalDate(params.row.createdat),
        },
        {
            headerName: "SIZE",
            field: "size",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            headerName: "PRICE",
            field: "price",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            headerName: "TOTAL",
            field: "total",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        {
            headerName: "MIX PRICE",
            field: "mixprice",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixprice),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixprice)
        },
        {
            headerName: "MIX TOTAL",
            field: "mixtotal",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixtotal),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixtotal)
        },
    ]

    const mainColumn = [
        {
            headerName: "KAPAN ID",
            field: "kapanid",
            headerAlign: "center",
            width: 460,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            align: "center",
        },
        {
            headerName: "TOTAL SIZE",
            field: "totalsize",
            headerAlign: "center",
            align: "center",
            width: 430,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.totalsize),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.totalsize),
        },
        {
            headerName: "MIX AVG PRICE",
            field: "mixavgprice",
            width: 430,
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixavgprice),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixavgprice)
        },
        {
            headerName: "MIX AVG TOTAL",
            field: "mixavgtotal",
            headerAlign: "center",
            width: 435,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            valueGetter: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixavgtotal),
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.mixavgtotal)
        }
    ];

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback(
        (row, detailReportData) => <DetailPanelContent mainRow={row} detailReportData={detailReportData} />,
        [],
    );

    function DetailPanelContent({ mainRow, detailReportData }) {
        let row = detailReportData.filter(data => data.kapanid === mainRow.kapanid);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "44vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        height: 'auto',
                        marginLeft: '16px',
                        marginTop: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={false}
                        density="compact"
                        columns={detailColumn}
                        rows={row}
                        getRowId={(row) => row.stoneid}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            maxHeight: '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    const columns = mainColumn.filter(iColumn => {
        if (iColumn.field === "mixavgprice" && !mixAvgPricePermission) return false;
        return true;
    });

    return (
        !Utility.isReadAllowed(local_permision) ? <NoPermission /> : (
            <div>
                {showSnackbar ? (
                    <CommonSnackbar
                        show={showSnackbar}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" : "Failed"}
                        handleClose={() => setShowSnackbar(false)}
                    />
                ) : null}

                {showStoneDetailModal ? (
                    <StoneDetailModal
                        show={showStoneDetailModal}
                        stoneId={stoneId}
                        handleClose={() => setShowStoneDetailModal(false)}
                    />
                ) : null}
                {showKapanDetailModal ? (
                    <KapanHistoryModal
                        show={showKapanDetailModal}
                        kapanId={kapanId}
                        close={() => setShowKapanDetailModal(false)}
                    />
                ) : null}
                {showPacketDetailModal ? (
                    <PacketHistoryModal
                        show={showPacketDetailModal}
                        packetId={packetId}
                        kapanId={kapanId}
                        close={() => setShowPacketDetailModal(false)}
                    />
                ) : null}

                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "100%",
                        position: "relative",
                        paddingTop: "1.5rem",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: '80vh'
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={false}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            height: "80vh",
                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold'
                        }}
                        density="compact"
                        loading={reportLoader}
                        slots={{ toolbar: GridToolbar }}
                        rows={reportData}
                        columns={columns}
                        getRowId={(row) => row.kapanid}
                        initialState={{
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={({ row }) => getDetailPanelContent(row, detailReportData)}
                    />

                    <Box
                        style={{
                            position: "absolute",
                            top: 32,
                            right: "270px",
                            display: "flex",
                            gap: "15px",
                            alignItems: "center"
                        }}
                    >

                        {reportLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={() => {
                                        handleSearch()
                                    }}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer" }}
                                />
                            </Tooltip>
                        )}

                    </Box>
                </Box>
            </div>)
    );
}

export default MixReportAvg;